import type {
  InventoryLocationBuilding,
  SerializedInventoryLocation,
  SerializedInventoryLocationBuilding,
} from '@enmon/commons/lib/tools';
import { BASE_REST_API_URL } from 'common/constants';
import { buildQuery } from '@enmon/commons/lib/tools/utils/buildQuery';
import type { StrapiApiResponse } from 'api/types/strapiApiResponse';
import type { BaseAPIParams } from '../../utils/BaseAPIParams';
import { BaseApiClient } from '../baseApiClient';

export type InventoryLocationBuildingFilter = BaseAPIParams<SerializedInventoryLocationBuilding> & {
  inventory_locations?: SerializedInventoryLocation['id'][];
};

export type LocationBuildingDTO = {
  inventory_location: number | string;
} & Pick<
  InventoryLocationBuilding,
  'name' | 'code' | 'backend_gateway_establishment_id' | 'address' | 'floor_area' | 'building_volume' | 'business_hours'
>;

export type FindInventoryLocationBuildingArg = InventoryLocationBuildingFilter;
export type FindInventoryLocationBuildingResponse = StrapiApiResponse<SerializedInventoryLocationBuilding>;

export class BuildingsApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('inventory-location-buildings', BASE_REST_API_URL, jwt);
  }

  public async find(
    filter: FindInventoryLocationBuildingArg = {},
    signal?: AbortSignal,
  ): Promise<FindInventoryLocationBuildingResponse> {
    const { inventory_locations, sort, page, pageSize, fields, ...queryFilter } = filter;

    const response = await this.axios.get<FindInventoryLocationBuildingResponse>('', {
      params: buildQuery<SerializedInventoryLocationBuilding>({
        sort,
        pagination: {
          page,
          pageSize,
        },
        filters: {
          ...queryFilter,
          inventory_location: {
            id: {
              $in: inventory_locations,
            },
          },
        },
        fields,
      }),
      signal,
    });

    return response.data;
  }

  public async create(data: LocationBuildingDTO): Promise<SerializedInventoryLocationBuilding> {
    const response = await this.axios.post<SerializedInventoryLocationBuilding>('', { data });

    return response.data;
  }

  public async update(
    id: SerializedInventoryLocationBuilding['id'],
    data: LocationBuildingDTO,
  ): Promise<SerializedInventoryLocationBuilding> {
    const response = await this.axios.put<SerializedInventoryLocationBuilding>(id.toString(), { data });

    return response.data;
  }

  public async delete(id: SerializedInventoryLocationBuilding['id']): Promise<void> {
    await this.axios.delete<SerializedInventoryLocationBuilding>(id.toString());
  }
}
